'use client'

import React, { useCallback, useMemo, useState } from 'react'
import { useLocale, useTranslations } from 'next-intl'

import { useGetCountryDataQuery } from '@/src/api/rtkQuery/countryApi'
import { useUpdateContactDataMutation } from '@/src/api/rtkQuery/profileApi'
import { TITLES } from '@/src/constants/travelers'
import { ProfileData } from '@/src/types/profile'
import useFormatDate from '@/src/hooks/date/useFormatDate'
import { buildProfileUpdatePayload } from '@/src/utils/profile/buildContactDataPayload'

import { EditProfile, EditProfileFormValues, EditProfileOption } from './editProfile'

const PROFILE_TITLE_VALUES = [...TITLES.ADT] as const

interface EditProfileWrapperProps {
    initialProfile: ProfileData
    onCancel: () => void
    onSave: () => void
}

function buildInitialFormValues(profile: ProfileData, formatDate: (date: string, format?: string) => string): EditProfileFormValues {
    const titleRaw = profile.title.replace(/\.$/, '')
    const dateOfBirthInput = profile.dateOfBirth ? formatDate(profile.dateOfBirth, 'YYYY-MM-DD') : ''

    return {
        title: titleRaw,
        firstName: profile.firstName,
        lastName: profile.lastName,
        gender: profile.gender || '',
        phoneCountryCode: profile.phone.countryCode,
        phoneNumber: profile.phone.number,
        email: profile.email,
        dateOfBirth: dateOfBirthInput,
        screenName: profile.screenName,
        addressLine1: profile.address.line1,
        city: profile.address.city,
        state: profile.address.state,
        postalCode: profile.address.postalCode,
        country: profile.address.country
    }
}

const EditProfileWrapper: React.FC<EditProfileWrapperProps> = ({ initialProfile, onCancel, onSave }) => {
    const locale = useLocale()
    const { formatDate } = useFormatDate()
    const t = useTranslations('account.profile')
    const tTitles = useTranslations('common.titles')
    const { data: countryData } = useGetCountryDataQuery()
    const [updateContactData, { isLoading: isSaving }] = useUpdateContactDataMutation()
    const initialValues = useMemo(() => buildInitialFormValues(initialProfile, formatDate), [initialProfile, formatDate])
    const [formValues, setFormValues] = useState<EditProfileFormValues>(initialValues)
    const [saveError, setSaveError] = useState<string | null>(null)

    const titleOptions: EditProfileOption[] = useMemo(
        () =>
            PROFILE_TITLE_VALUES.map(value => ({
                value,
                label: tTitles(value.toLowerCase())
            })),
        [tTitles]
    )

    const countryCodeOptions: EditProfileOption[] = useMemo(() => {
        if (!countryData) return []
        const lang = locale === 'ar' ? 'ar' : 'en'
        return countryData.map(item => ({
            value: item.mobileCountryCode,
            label: `+${item.mobileCountryCode} (${item[lang]})`
        }))
    }, [countryData, locale])

    const genderOptions: EditProfileOption[] = useMemo(
        () => [
            { value: 'Male', label: t('genderMale') },
            { value: 'Female', label: t('genderFemale') },
            { value: 'Other', label: t('genderOther') }
        ],
        [t]
    )

    const handleChange = useCallback((patch: Partial<EditProfileFormValues>) => {
        setFormValues(prev => ({ ...prev, ...patch }))
        setSaveError(null)
    }, [])

    const handleSave = useCallback(async () => {
        try {
            setSaveError(null)
            const payload = buildProfileUpdatePayload(formValues)
            await updateContactData(payload).unwrap()
            onSave()
        } catch {
            setSaveError(t('saveFailed'))
        }
    }, [formValues, updateContactData, onSave, t])

    return (
        <EditProfile
            values={formValues}
            t={t}
            titleOptions={titleOptions}
            genderOptions={genderOptions}
            countryCodeOptions={countryCodeOptions}
            isSaving={isSaving}
            saveError={saveError}
            onChange={handleChange}
            onCancel={onCancel}
            onSave={handleSave}
        />
    )
}

export { EditProfileWrapper }
export type { EditProfileWrapperProps }
export default EditProfileWrapper
